import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import type { Account } from '../types';
import { trackEvent } from '../lib/analytics';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { OdsBadge } from './orderly/OdsBadge';

function formatUSD(val: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(val);
}

interface Props {
  account: Account;
  onClick: () => void;
}

export default function AccountCard({ account, onClick }: Props) {
  const totalPnl = account.positions.reduce((sum, p) => sum + p.unrealizedPnl, 0);
  const [copied, setCopied] = useState(false);
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (copyResetTimeoutRef.current) clearTimeout(copyResetTimeoutRef.current);
  }, []);

  async function handleCopyAccountId(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    await navigator.clipboard.writeText(account.accountId);
    setCopied(true);
    if (copyResetTimeoutRef.current) clearTimeout(copyResetTimeoutRef.current);
    copyResetTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
  }

  useEffect(() => {
    trackEvent('component_viewed', {
      component: 'AccountCard',
      account_id: account.accountId,
      account_type: account.type,
    });
  }, [account.accountId, account.type]);

  return (
    <div
      className="group rounded-xl text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-primary)]"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <Card className="border-[var(--border-subtle)] bg-[var(--bg-surface-primary)] py-0 transition group-hover:border-[var(--border-primary)] group-hover:bg-[var(--bg-primary-subtle)]">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center gap-3">
            <span className="min-w-0 flex-1 truncate text-base font-semibold text-[var(--fg-base)]">{account.name}</span>
            <OdsBadge type={account.type === 'main' ? 'brand' : 'subtle'}>
              {account.type === 'main' ? 'Main' : 'Sub'}
            </OdsBadge>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <span className="block text-xs text-[var(--fg-secondary)]">Account value</span>
              <span className="block text-sm font-medium text-[var(--fg-base)]">{formatUSD(account.totalAssets)}</span>
            </div>
            <div className="space-y-1">
              <span className="block text-xs text-[var(--fg-secondary)]">Unrealized PnL</span>
              <span className={cn('block text-sm font-medium', totalPnl >= 0 ? 'text-[var(--fg-positive)]' : 'text-[var(--fg-negative)]')}>
                {totalPnl >= 0 ? '+' : ''}{formatUSD(totalPnl)}
              </span>
            </div>
            <div className="space-y-1">
              <span className="block text-xs text-[var(--fg-secondary)]">Positions</span>
              <span className="block text-sm font-medium text-[var(--fg-base)]">{account.positions.length}</span>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3 border-t border-[var(--border-subtle)] pt-4">
            <span className="min-w-0 flex-1 break-all font-mono text-[11px] text-[var(--fg-secondary)]">
              {account.accountId}
            </span>
            <button
              type="button"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[var(--fg-secondary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg-base)] focus-visible:ring-2 focus-visible:ring-[var(--border-primary)]"
              onClick={(event) => void handleCopyAccountId(event)}
              aria-label={copied ? 'Account ID copied' : 'Copy account ID'}
              title={copied ? 'Copied' : 'Copy account ID'}
            >
              {copied ? <Check className="h-4 w-4 text-[var(--fg-positive)]" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
